import { DestroyRef, inject, Injectable } from '@angular/core';
import Dexie, { liveQuery, Table } from 'dexie';
import { EncryptionService } from '@evorait/pace-encryption';
import { catchError, concatMap, filter, map, mergeMap, switchMap, take } from 'rxjs/operators';
import { defer, from, Observable, of, throwError } from 'rxjs';
import { OtherTaskId, TaskDexieEntry } from './odata-other-tasks.interface';
import { LogInstanceToken } from '@evorait/pace-logging';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

type UpdateStats = {
    updated: number;
    added: number;
    deleted: number;
};

@Injectable({ providedIn: 'root' })
export class OtherTasksDexieDbService extends Dexie {
    private readonly encryption = inject(EncryptionService);
    private readonly log = inject(LogInstanceToken);
    private destroyRef = inject(DestroyRef);

    private static readonly CONCURRENT_MAX_AGE_MS = 300 * 24 * 60 * 60 * 1000;
    private static readonly CONCURRENT_MAX_DELETED_MS = 30 * 24 * 60 * 60 * 1000;

    public static readonly DB_NAME = 'OtherTaskDB';
    public static readonly DB_TABLE = 'originalDates';

    private static readonly VERSION_ONE = 1;

    private originalDates: Table<TaskDexieEntry<string>, [string, string]>;

    public constructor() {
        super(OtherTasksDexieDbService.DB_NAME);
        this.version(OtherTasksDexieDbService.VERSION_ONE).stores({
            [OtherTasksDexieDbService.DB_TABLE]: '[notification+task],created,updated,deleted,data',
        });
        this.originalDates = this.table<TaskDexieEntry<string>, [string, string]>(OtherTasksDexieDbService.DB_TABLE);
    }

    public getLiveData<T>(id: OtherTaskId): Observable<T | undefined> {
        return this.waitForEncryption().pipe(
            switchMap(() =>
                liveQuery(() =>
                    this.originalDates
                        .get([id.notification, id.task])
                        .then((entity) => (entity && !entity.deleted ? entity : undefined)),
                ),
            ),
            mergeMap(async (entity) => (entity ? (await this.prepareDbReadAsync<T>(entity)).data : undefined)),
            catchError((error) => {
                this.log.error(`${this.constructor.name}: getLiveData: Error on reading from database: ${error}`);
                return of(undefined);
            }),
        );
    }

    public getAllEntries(): Observable<string[]> {
        return this.waitForEncryption().pipe(
            switchMap(() => from(this.originalDates.toArray())),
            map((entities) => entities.map((entity) => this.getIdOfAllPickedTasks(entity))),
            catchError((error) => {
                this.log.error(`${this.constructor.name}: getAllEntries: Error on reading from database: ${error}`);
                return of([]);
            }),
        );
    }

    public upsert<T>(id: OtherTaskId, otherTask: T): Observable<[string, string]> {
        const primaryKey = [id.notification, id.task] as [string, string];
        return this.waitForEncryption().pipe(
            concatMap(() => defer(() => this.originalDates.get(primaryKey))),
            concatMap(async (existingRecord) => {
                const now = Date.now();
                const recordToPut = {
                    notification: id.notification,
                    task: id.task,
                    deleted: 0,
                    updated: now,
                    created: existingRecord?.created ?? now,
                    data: otherTask,
                };
                return defer(async () => this.originalDates.put(await this.prepareDbWriteAsync(recordToPut)));
            }),
            map(() => primaryKey),
            catchError((error) => {
                this.log.error(`${this.constructor.name}: upsert: Error while saving entry`, error);
                return of(primaryKey);
            }),
        );
    }

    public remove(id: OtherTaskId, purge = false): Observable<number> {
        const primaryKey = [id.notification, id.task] as [string, string];

        const dbOperation$ = defer(() => {
            if (purge) {
                return this.originalDates.delete(primaryKey);
            } else {
                return this.originalDates.update(primaryKey, { deleted: Date.now() });
            }
        });

        return dbOperation$.pipe(
            map((result) => {
                return result === undefined ? 1 : result;
            }),
            catchError((error) => {
                this.log.error(`${this.constructor.name}: remove: Error while removing entry`, error);
                return of(0);
            }),
        );
    }

    public syncLocalAndRemote<T>(remote: string): Observable<string> {
        return this.waitForEncryption().pipe(
            switchMap(() => from(this.originalDates.toArray())),
            mergeMap(async (local) => Promise.all(local.map((date) => this.prepareDbReadAsync<T>(date)))),
            map((local) => {
                const remoteDataString = remote && remote.trim() ? remote : '[]';
                return this.syncStates(local, JSON.parse(remoteDataString) as Array<TaskDexieEntry<T>>);
            }),
            map((results) => {
                const currentDate = Date.now();
                return results.filter(
                    (entry) =>
                        entry.created > currentDate - OtherTasksDexieDbService.CONCURRENT_MAX_AGE_MS &&
                        (!entry.deleted ||
                            entry.deleted > currentDate - OtherTasksDexieDbService.CONCURRENT_MAX_DELETED_MS),
                );
            }),
            map((results) => JSON.stringify(results)),
            catchError((e: unknown) => {
                this.log.error(`${this.constructor.name}: syncLocalAndRemote: Error while syncing`, e);
                return of('[]');
            }),
        );
    }

    private syncStates<T>(local: Array<TaskDexieEntry<T>>, remote: Array<TaskDexieEntry<T>>): Array<TaskDexieEntry<T>> {
        const localMap = new Map(local.map((e) => [`${e.notification}::${e.task}`, e]));
        const remoteMap = new Map(remote.map((e) => [`${e.notification}::${e.task}`, e]));
        const updateLocal = new Set<TaskDexieEntry<T>>();
        const addLocal: Array<TaskDexieEntry<T>> = [];
        const deleteLocal: Array<[string, string]> = [];

        // sync remote -> local
        for (const remoteEntity of remote) {
            const localEntity = localMap.get(`${remoteEntity.notification}::${remoteEntity.task}`);
            if (!localEntity) {
                if (!remoteEntity.deleted) {
                    addLocal.push(remoteEntity);
                }
            } else {
                if (remoteEntity.updated > localEntity.updated) {
                    updateLocal.add(remoteEntity);
                } else {
                    remoteEntity.data = localEntity.data;
                    remoteEntity.updated = localEntity.updated;
                }
                if (remoteEntity.deleted && !localEntity.deleted) {
                    updateLocal.add(remoteEntity);
                }
                if (localEntity.deleted && !remoteEntity.deleted) {
                    remoteEntity.deleted = localEntity.deleted;
                    remoteEntity.updated = Math.max(remoteEntity.updated, localEntity.updated);
                }
            }
        }

        // sync local -> remote
        for (const localEntity of local) {
            const remoteEntity = remoteMap.get(`${localEntity.notification}::${localEntity.task}`);
            if (!remoteEntity) {
                if (localEntity.deleted) {
                    // it's deleted but has never seen remote, so we can savely delete here
                    deleteLocal.push([localEntity.notification, localEntity.task]);
                } else {
                    remote.push(localEntity);
                }
            }
            // the case that an entity exists on both sides has been covered in remote -> local
        }

        this.applyLocalUpdates$(updateLocal, addLocal, deleteLocal)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe();
        return remote;
    }

    private applyLocalUpdates$<T>(
        updateLocal: Set<TaskDexieEntry<T>>,
        addLocal: Array<TaskDexieEntry<T>>,
        deleteLocal: Array<[string, string]>,
    ): Observable<UpdateStats> {
        if (updateLocal.size === 0 && addLocal.length === 0 && deleteLocal.length === 0) {
            return of({ updated: 0, added: 0, deleted: 0 });
        }
        return defer(() =>
            from(
                this.transaction('rw', this.originalDates, async () => {
                    let updated = 0;
                    let added = 0;
                    let deleted = 0;
                    if (updateLocal.size) {
                        await this.originalDates.bulkPut(await this.prepareDbWriteManyAsync([...updateLocal]));
                        updated = updateLocal.size;
                    }
                    if (addLocal.length) {
                        await this.originalDates.bulkPut(await this.prepareDbWriteManyAsync(addLocal));
                        added = addLocal.length;
                    }
                    if (deleteLocal.length) {
                        await this.originalDates.bulkDelete(deleteLocal);
                        deleted = deleteLocal.length;
                    }
                    return { updated, added, deleted };
                }),
            ),
        ).pipe(
            catchError((error) => {
                this.log.error(
                    `${this.constructor.name}: applyLocalUpdates$: failed to apply local updates (transaction)`,
                    error,
                );
                return throwError(() => error);
            }),
        );
    }

    private waitForEncryption(): Observable<boolean> {
        return this.encryption.selectInitialized.pipe(filter(Boolean), take(1));
    }

    private async prepareDbWriteManyAsync<T>(
        entities: Array<TaskDexieEntry<T>>,
    ): Promise<Array<TaskDexieEntry<string>>> {
        return Promise.all(entities.map((e) => this.prepareDbWriteAsync(e)));
    }

    private async prepareDbWriteAsync<T>(entity: TaskDexieEntry<T>): Promise<TaskDexieEntry<string>> {
        return {
            ...entity,
            data: await this.encryption.encryptAsync(JSON.stringify(entity.data)),
        };
    }

    private async prepareDbReadAsync<T>(entity: TaskDexieEntry<string>): Promise<TaskDexieEntry<T>> {
        return {
            ...entity,
            data: JSON.parse(await this.encryption.decryptAsync(entity.data)) as T,
        };
    }

    private getIdOfAllPickedTasks(entity: TaskDexieEntry<string>): string {
        return `${entity.notification}::${entity.task}`;
    }
}
